import { Controller, FieldValues, useFormContext } from "react-hook-form";

import {
  StepType,
  StepWithOptions,
  StepWithFields,
  StepWithCustomContent,
  Field,
  Option,
} from "../types";

import { ContentProps } from "./types";
import { Fields } from "./Fields";

import { CardWithCheckbox, CardWithRadio } from "@/components/ui/Cards";

export const Content = <T extends FieldValues>({
  step,
  onStepOptionSelect,
  onItemHover,
  size,
  className,
}: ContentProps<T>) => {
  const form = useFormContext();
  const { id, type } = step;

  const getStepOptions = () => {
    if (type === StepType.FIELDS) {
      return (step as StepWithFields).options;
    } else if (type === StepType.SINGLE_SELECT || type === StepType.MULTI_SELECT) {
      return (step as StepWithOptions).options;
    }
    return [];
  };

  const validation = "validation" in step ? step.validation : undefined;
  const options = getStepOptions();

  const rules = {
    validate: validation,
  };

  const handleOnMultipleChange = (
    optionId: string,
    formOnChange: (value: string[]) => void,
    values: string[],
  ) => {
    if (values?.includes(optionId)) {
      formOnChange(values.filter(value => value !== optionId));
    } else {
      formOnChange([...(values || []), optionId]);
    }

    onStepOptionSelect?.(optionId);
    form.trigger(id);
  };

  const getContent = () => {
    // eslint-disable-next-line default-case
    switch (type) {
      case StepType.CUSTOM_STEP:
        return (step as StepWithCustomContent).getCustomContent(form.getValues());
      case StepType.SINGLE_SELECT:
        return (
          <Controller
            name={id}
            control={form.control}
            rules={rules}
            render={({ field }) => (
              <>
                {(options as Option[]).map(option => (
                  <CardWithRadio
                    id={option.id}
                    key={option.id}
                    title={option.title}
                    description={option.description}
                    onSelect={() => {
                      field.onChange(option.id);
                      form.trigger(id);
                    }}
                    checked={field.value === option.id}
                  />
                ))}
              </>
            )}
          />
        );
      case StepType.MULTI_SELECT:
        return (
          <Controller
            name={id}
            control={form.control}
            rules={rules}
            render={({ field }) => (
              <>
                {(options as Option[]).map(option => (
                  <CardWithCheckbox
                    id={option.id}
                    onHover={onItemHover}
                    key={option.id}
                    title={option.title}
                    description={option.description}
                    onSelect={value => handleOnMultipleChange(value, field.onChange, field.value)}
                    checked={field.value?.includes(option.id)}
                  />
                ))}
              </>
            )}
          />
        );
      case StepType.FIELDS:
        return <Fields stepId={id} fields={options as Field[]} size={size} />;
    }
  };

  return (
    <div
      className={`flex max-w-[853px] flex-col gap-4 ${className} ${
        size === "dialog" ? "h-full min-h-0 overflow-y-auto" : "h-full overflow-scroll"
      }`}
    >
      {getContent()}
    </div>
  );
};
